import {
	Cancel,
	CheckCircleOutline,
	InboxOutlined,
	Payments,
	RequestQuote,
	RestartAlt,
} from '@mui/icons-material';
import { Alert, Box, Chip, Menu, MenuItem, Paper, Typography } from '@mui/material';
import { MouseEvent, ReactNode, useState } from 'react';
import { IndicacaoSolicitacaoLista } from '../../../services/indicacao';
import { formatCurrencyBRL } from '../../../utils/mask/MaskUtils';
import { corStatusSolicitacao, formatDate, textoStatusSolicitacao } from '../indicacao.utils';
import { DataGridColumn, DataGridView } from './DataGridView';
import { EmptyState } from './EmptyState';
import { IndicacaoButton } from './IndicacaoButton';
import { SectionHeader } from './SectionHeader';

interface SolicitacoesGridProps {
	isAdmin: boolean;
	solicitacoes: IndicacaoSolicitacaoLista[];
	emptyText: string;
	decidindoId: number | null;
	pagandoId: number | null;
	onAceitar: (item: IndicacaoSolicitacaoLista) => void;
	onRecusar: (id: number) => void;
	onPagar: (id: number, paga: boolean) => void;
	alertForaDoFiltro?: boolean;
	titulo: string;
	descricao: string;
	headerAction?: ReactNode;
}

const StatusChip = ({ status }: { status: string }) => (
	<Chip
		size="small"
		label={textoStatusSolicitacao[status] || status}
		sx={{
			fontWeight: 700,
			color: corStatusSolicitacao[status]?.color ?? 'text.secondary',
			bgcolor: corStatusSolicitacao[status]?.bgcolor ?? '#f1f5f9',
		}}
	/>
);

const abrirDetalheAnuncio = (idAnuncio: number, isCombo: boolean) => {
	const detalhe = isCombo
		? `/anuncios/detalhe/combo/${idAnuncio}`
		: `/anuncios/detalhe/${idAnuncio}`;
	window.open(
		`${process.env.PUBLIC_URL || ''}${detalhe}`,
		'_blank',
		'noopener,noreferrer'
	);
};

const AnunciosMenuChip = ({ item }: { item: IndicacaoSolicitacaoLista }) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const anuncios = item.anuncios ?? [];
	const semAnuncios = anuncios.length === 0;
	const rotulo =
		anuncios.length === 1 ? '1 anúncio' : `${anuncios.length} anúncios`;

	const handleAbrir = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	return (
		<>
			<Chip
				size="small"
				label={semAnuncios ? '0' : rotulo}
				disabled={semAnuncios}
				onClick={semAnuncios ? undefined : handleAbrir}
				sx={{
					fontWeight: 700,
					bgcolor: '#f1f5f9',
					cursor: semAnuncios ? 'default' : 'pointer',
				}}
			/>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				{anuncios.map((anuncio) => (
					<MenuItem
						key={anuncio.id_anuncio}
						onClick={() => {
							abrirDetalheAnuncio(anuncio.id_anuncio, anuncio.is_combo);
							setAnchorEl(null);
						}}
					>
						Anúncio #{anuncio.id_anuncio}
					</MenuItem>
				))}
			</Menu>
		</>
	);
};

const PagamentoChip = ({ item }: { item: IndicacaoSolicitacaoLista }) =>
	item.status === 'ACEITA' ? (
		<Chip
			size="small"
			label={item.paga ? 'Paga' : 'Não paga'}
			sx={{
				fontWeight: 700,
				color: item.paga ? 'rgb(5, 150, 105)' : 'rgb(100, 116, 139)',
				bgcolor: item.paga ? 'rgba(5, 150, 105, 0.12)' : '#f1f5f9',
			}}
		/>
	) : (
		<>{'-'}</>
	);

const AcoesAdmin = ({
	item,
	decidindoId,
	pagandoId,
	onAceitar,
	onRecusar,
	onPagar,
}: {
	item: IndicacaoSolicitacaoLista;
	decidindoId: number | null;
	pagandoId: number | null;
	onAceitar: (item: IndicacaoSolicitacaoLista) => void;
	onRecusar: (id: number) => void;
	onPagar: (id: number, paga: boolean) => void;
}) => {
	const ocupado = decidindoId !== null || pagandoId !== null;

	if (item.status === 'SOLICITADA') {
		return (
			<Box display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap">
				<IndicacaoButton
					size="small"
					variant="success"
					label="Aceitar"
					icon={<CheckCircleOutline fontSize="small" />}
					disabled={ocupado}
					loading={decidindoId === item.id}
					onClick={() => onAceitar(item)}
				/>
				<IndicacaoButton
					size="small"
					variant="danger"
					label="Recusar"
					icon={<Cancel fontSize="small" />}
					disabled={ocupado}
					onClick={() => onRecusar(item.id)}
				/>
			</Box>
		);
	}

	if (item.status === 'ACEITA') {
		return (
			<IndicacaoButton
				size="small"
				variant={item.paga ? 'outlined' : 'success'}
				label={item.paga ? 'Estornar pagamento' : 'Marcar como paga'}
				icon={item.paga ? <RestartAlt fontSize="small" /> : <Payments fontSize="small" />}
				disabled={ocupado}
				loading={pagandoId === item.id}
				onClick={() => onPagar(item.id, !item.paga)}
			/>
		);
	}

	return <>-</>;
};

export function SolicitacoesGrid({
	isAdmin,
	solicitacoes,
	emptyText,
	decidindoId,
	pagandoId,
	onAceitar,
	onRecusar,
	onPagar,
	alertForaDoFiltro,
	titulo,
	descricao,
	headerAction,
}: SolicitacoesGridProps) {
	const columns: DataGridColumn<IndicacaoSolicitacaoLista>[] = [
		...(isAdmin
			? [
					{
						id: 'solicitante',
						label: 'Solicitante',
						render: (item: IndicacaoSolicitacaoLista) => item.nome_solicitante || '—',
					},
			  ]
			: []),
		{
			id: 'data',
			label: 'Data do pedido',
			render: (item) => formatDate(item.data_solicitacao),
		},
		{
			id: 'decisao',
			label: 'Decisão em',
			render: (item) => (item.data_decisao ? formatDate(item.data_decisao) : '—'),
		},
		{
			id: 'situacao',
			label: 'Situação',
			render: (item) => (
				<Box>
					<StatusChip status={item.status} />
					{item.observacao_solicitante && (
						<Typography fontSize="0.75rem" color="text.secondary" mt={0.5}>
							{item.observacao_solicitante}
						</Typography>
					)}
				</Box>
			),
		},
		{
			id: 'pagamento',
			label: 'Pagamento',
			render: (item) => <PagamentoChip item={item} />,
		},
		{
			id: 'valor',
			label: 'Valor solicitado',
			align: 'right',
			render: (item) => formatCurrencyBRL(item.valor_solicitado),
		},
		{
			id: 'anuncios',
			label: 'Anúncios',
			align: 'right',
			render: (item) => <AnunciosMenuChip item={item} />,
		},
		...(isAdmin
			? [
					{
						id: 'acoes',
						label: 'Ações',
						align: 'right' as const,
						render: (item: IndicacaoSolicitacaoLista) => (
							<AcoesAdmin
								item={item}
								decidindoId={decidindoId}
								pagandoId={pagandoId}
								onAceitar={onAceitar}
								onRecusar={onRecusar}
								onPagar={onPagar}
							/>
						),
					},
			  ]
			: []),
	];

	return (
		<Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #f1f5f9' }}>
			<Box mb={2}>
				<SectionHeader
					icon={<RequestQuote />}
					titulo={titulo}
					descricao={descricao}
					acao={headerAction}
				/>
			</Box>
			{alertForaDoFiltro && (
				<Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
					Há uma solicitação em aberto fora do período filtrado.
				</Alert>
			)}
			<DataGridView
				columns={columns}
				rows={solicitacoes}
				getRowId={(item) => item.id}
				empty={<EmptyState icon={<InboxOutlined />} texto={emptyText} />}
				renderMobileCard={(item) => (
					<Box
						sx={{
							p: 1.75,
							borderRadius: 2.5,
							border: '1px solid #f1f5f9',
							display: 'flex',
							flexDirection: 'column',
							gap: 1.25,
						}}
					>
						<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
							<Box minWidth={0}>
								{isAdmin && (
									<Typography fontWeight={800} noWrap>
										{item.nome_solicitante || '—'}
									</Typography>
								)}
								<Typography fontSize="0.8rem" color="text.secondary">
									Pedido em {formatDate(item.data_solicitacao)}
								</Typography>
							</Box>
							<Typography fontWeight={800} color="rgb(5, 150, 105)" whiteSpace="nowrap">
								{formatCurrencyBRL(item.valor_solicitado)}
							</Typography>
						</Box>
						<Box display="flex" gap={1} flexWrap="wrap">
							<StatusChip status={item.status} />
							<PagamentoChip item={item} />
							<AnunciosMenuChip item={item} />
						</Box>
						{isAdmin && (
							<AcoesAdmin
								item={item}
								decidindoId={decidindoId}
								pagandoId={pagandoId}
								onAceitar={onAceitar}
								onRecusar={onRecusar}
								onPagar={onPagar}
							/>
						)}
					</Box>
				)}
			/>
		</Paper>
	);
}
